class StoriesComponent extends HTMLElement {
	constructor() {
		super();
		this.currentStoryIndex = 0;
		this.currentSlideIndex = 0;
		this.stories = [];
		this.progressTimer = null;
		this.slideDuration = 5000; // 5 seconds per slide (default)
		this.currentSlideDuration = 5000; // Current slide duration (can vary for videos)
		this.touchStartX = 0;
		this.touchStartY = 0;
		this.touchEndX = 0;
		this.touchEndY = 0;
		this.isOpen = false;
		this.videoProgressInterval = null;
		this.isPaused = false;
		this.holdTimer = null;
		this.isHolding = false;
		this.holdDelay = 200; // Delay before hold is detected (ms)
		this.isDragging = false;
		this.dragStartY = 0;
		this.dragThreshold = 100; // Pixels to drag down before closing
	}

	connectedCallback() {
		this.init();
	}

	disconnectedCallback() {
		this.clearProgress();
		this.removeEventListeners();
	}

	init() {
		this.stories = Array.from(this.querySelectorAll('.story-preview'));
		this.modal = this.querySelector('.stories-modal');
		this.bindEvents();
		this.loadCSS();
		this.preloadMedia();
	}

	loadCSS() {
		// Load the CSS file if not already loaded
		if (!document.querySelector('link[href*="component-stories.css"]')) {
			const link = document.createElement('link');

			link.rel = 'stylesheet';
			link.href = `${window.Shopify?.routes?.root || '/'}assets/component-stories.css`;

			document.head.appendChild(link);
		}
	}

	preloadMedia() {
		// Preload images and videos for faster loading
		this.stories.forEach((story, index) => {
			const storyContent = story.querySelector('.story-content');

			if (!storyContent) return;

			const slides = storyContent.querySelectorAll('.story-slide');

			slides.forEach((slide, slideIndex) => {
				// Preload images
				const images = slide.querySelectorAll('.story-slide__image');

				images.forEach((img) => {
					if (img.src) {
						const preloadImg = new Image();
						preloadImg.src = img.src;
					}
				});

				// Preload videos (set preload attribute and load metadata)
				const videos = slide.querySelectorAll('.story-slide__video');

				videos.forEach((video) => {
					video.preload = 'metadata';
					video.load();

					// Add event listener for when video metadata is loaded
					video.addEventListener('loadedmetadata', () => {
						// Store video duration for progress bar sync
						video.dataset.duration = video.duration * 1000; // Convert to milliseconds
					});
				});
			});
		});
	}

	bindEvents() {
		// Story preview clicks
		this.stories.forEach((story, index) => {
			story.addEventListener('click', () => this.openStory(index));
		});

		// Modal controls
		const closeBtn = this.querySelector('.stories-modal__close');
		const prevBtn = this.querySelector('.stories-modal__nav--prev');
		const nextBtn = this.querySelector('.stories-modal__nav--next');
		const overlay = this.querySelector('.stories-modal__overlay');

		closeBtn?.addEventListener('click', () => this.closeModal());
		prevBtn?.addEventListener('click', () => this.previousSlide());
		nextBtn?.addEventListener('click', () => this.nextSlide());
		overlay?.addEventListener('click', () => this.closeModal());

		// Keyboard navigation
		this.handleKeydown = (e) => {
			if (this.isOpen) {
				if (e.key === 'Escape') this.closeModal();
				if (e.key === 'ArrowLeft') this.previousSlide();
				if (e.key === 'ArrowRight') this.nextSlide();
				if (e.key === ' ') {
					e.preventDefault();
					this.toggleProgress();
				}
			}
		};

		// Touch events for swipe navigation and hold-to-pause
		this.handleTouchStart = (e) => {
			if (this.isOpen) {
				this.touchStartX = e.changedTouches[0].screenX;
				this.touchStartY = e.changedTouches[0].screenY;
				this.dragStartY = e.changedTouches[0].clientY;
				this.isDragging = false;

				// Start hold timer for pause functionality
				const touch = e.changedTouches[0];
				const target = document.elementFromPoint(touch.clientX, touch.clientY);

				// Only activate hold-to-pause in the main content area
				if (target && target.closest('.stories-modal__slides-container')) {
					this.startHoldTimer();
				}
			}
		};

		this.handleTouchEnd = (e) => {
			if (this.isOpen) {
				// Clear hold timer and resume if was holding
				this.endHoldTimer();

				this.touchEndX = e.changedTouches[0].screenX;
				this.touchEndY = e.changedTouches[0].screenY;

				// Check for vertical drag to close
				const verticalDistance = this.touchEndY - this.touchStartY;
				const horizontalDistance = Math.abs(this.touchEndX - this.touchStartX);

				// Close modal if dragged down significantly and vertical movement > horizontal
				if (verticalDistance > this.dragThreshold && verticalDistance > horizontalDistance) {
					this.closeModal();
					return;
				}

				// Only handle horizontal swipe if not holding and not dragging vertically
				if (!this.isHolding && !this.isDragging) {
					this.handleSwipe();
				}

				this.isHolding = false;
				this.isDragging = false;

				// Reset modal transform and background
				const container = this.modal.querySelector('.stories-modal__container');

				if (container) {
					container.style.transform = '';
				}
				this.modal.style.backgroundColor = '';
			}
		};

		// Touch move to handle drag and cancel hold if swiping
		this.handleTouchMove = (e) => {
			if (this.isOpen) {
				// Prevent default to stop body scroll
				e.preventDefault();

				const currentY = e.changedTouches[0].clientY;
				const currentX = e.changedTouches[0].screenX;
				const moveDistanceX = Math.abs(currentX - this.touchStartX);
				const moveDistanceY = currentY - this.dragStartY;

				// Cancel hold timer if user moves finger too much
				if (this.holdTimer && (moveDistanceX > 20 || Math.abs(moveDistanceY) > 20)) {
					this.endHoldTimer();
				}

				// Handle vertical drag for close gesture
				if (moveDistanceY > 0 && moveDistanceY > moveDistanceX) {
					this.isDragging = true;

					// Apply transform to modal container to show drag feedback
					const container = this.modal.querySelector('.stories-modal__container');
					const dragRatio = Math.min(moveDistanceY / this.dragThreshold, 1);
					const opacity = 1 - dragRatio * 0.5;

					container.style.transform = `translateY(${moveDistanceY}px)`;
					this.modal.style.backgroundColor = `rgba(0, 0, 0, ${0.95 * opacity})`;
				}
			}
		};

		document.addEventListener('keydown', this.handleKeydown);
		document.addEventListener('touchstart', this.handleTouchStart, { passive: true });
		document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
		document.addEventListener('touchend', this.handleTouchEnd, { passive: true });

		// Mouse events for desktop hold-to-pause
		this.handleMouseDown = (e) => {
			if (this.isOpen && e.target.closest('.stories-modal__slides-container')) {
				this.startHoldTimer();
			}
		};

		this.handleMouseUp = (e) => {
			if (this.isOpen) {
				this.endHoldTimer();
				this.isHolding = false;
			}
		};

		document.addEventListener('mousedown', this.handleMouseDown);
		document.addEventListener('mouseup', this.handleMouseUp);

		// Video control events (delegated)
		this.addEventListener('click', (e) => {
			if (e.target.closest('.story-video__play-pause')) {
				this.toggleVideoPlayPause();
			} else if (e.target.closest('.story-video__mute-unmute')) {
				this.toggleVideoMute();
			}
		});
	}

	toggleVideoPlayPause() {
		const video = this.getCurrentVideo();
		const playPauseBtn = this.querySelector('.story-video__play-pause');

		if (video && playPauseBtn) {
			if (video.paused) {
				video.play();
				playPauseBtn.classList.add('playing');
				this.startProgress(); // Resume progress
			} else {
				video.pause();
				playPauseBtn.classList.remove('playing');
				this.clearProgress(); // Pause progress
			}
		}
	}

	toggleVideoMute() {
		const video = this.getCurrentVideo();
		const muteBtn = this.querySelector('.story-video__mute-unmute');

		if (video && muteBtn) {
			video.muted = !video.muted;
			if (video.muted) {
				muteBtn.classList.add('muted');
			} else {
				muteBtn.classList.remove('muted');
			}
		}
	}

	removeEventListeners() {
		document.removeEventListener('keydown', this.handleKeydown);
		document.removeEventListener('touchstart', this.handleTouchStart);
		document.removeEventListener('touchmove', this.handleTouchMove);
		document.removeEventListener('touchend', this.handleTouchEnd);
		document.removeEventListener('mousedown', this.handleMouseDown);
		document.removeEventListener('mouseup', this.handleMouseUp);
	}

	startHoldTimer() {
		// Clear any existing timer
		this.endHoldTimer();

		// Start timer to detect hold
		this.holdTimer = setTimeout(() => {
			this.isHolding = true;
			this.pauseStory();
		}, this.holdDelay);
	}

	endHoldTimer() {
		if (this.holdTimer) {
			clearTimeout(this.holdTimer);
			this.holdTimer = null;
		}

		// Resume if was holding
		if (this.isHolding && this.isPaused) {
			this.resumeStory();
		}
	}

	pauseStory() {
		if (this.isPaused) return;

		this.isPaused = true;

		// Pause progress
		this.clearProgress();

		// Pause video if playing
		const video = this.getCurrentVideo();
		if (video && !video.paused) {
			video.pause();
		}

		// Add visual feedback
		this.modal.classList.add('stories-paused');
	}

	resumeStory() {
		if (!this.isPaused) return;

		this.isPaused = false;

		// Resume video if exists
		const video = this.getCurrentVideo();

		if (video) {
			video.play().catch((error) => {
				console.warn('Video resume failed:', error);
			});
		}

		// Resume progress
		this.startProgress();

		// Remove visual feedback
		this.modal.classList.remove('stories-paused');
	}

	handleSwipe() {
		const swipeThreshold = 50;
		const swipeDistance = this.touchStartX - this.touchEndX;

		if (Math.abs(swipeDistance) > swipeThreshold) {
			if (swipeDistance > 0) {
				// Swipe left - next slide
				this.nextSlide();
			} else {
				// Swipe right - previous slide
				this.previousSlide();
			}
		}
	}

	openStory(storyIndex) {
		this.currentStoryIndex = storyIndex;
		this.currentSlideIndex = 0;
		this.isOpen = true;

		// Prevent body scroll
		this.preventBodyScroll();

		const story = this.stories[storyIndex];
		const storyContent = story.querySelector('.story-content');
		const slides = storyContent.querySelectorAll('.story-slide');

		// Clear and populate modal
		const slidesContainer = this.querySelector('.stories-modal__slides-container');

		slidesContainer.innerHTML = storyContent.innerHTML;

		// Create progress bars
		this.createProgressBars(slides.length);

		// Show modal
		this.modal.style.display = 'block';
		document.body.style.overflow = 'hidden';

		// Start first slide
		this.showSlide(0);
		this.startProgress();

		// Dispatch custom event
		this.dispatchEvent(
			new CustomEvent('story:opened', {
				detail: { storyIndex, storyElement: story },
			})
		);
	}

	createProgressBars(count) {
		const progressContainer = this.querySelector('.stories-modal__progress-bars');

		progressContainer.innerHTML = '';

		for (let i = 0; i < count; i++) {
			const progressBar = document.createElement('div');

			progressBar.className = 'stories-modal__progress-bar';
			progressBar.innerHTML = '<div class="stories-modal__progress-fill"></div>';
			progressContainer.appendChild(progressBar);
		}
	}

	showSlide(slideIndex) {
		const slides = this.querySelectorAll('.stories-modal__slides-container .story-slide');

		// Pause all videos first
		this.pauseAllVideos();

		slides.forEach((slide, index) => {
			slide.style.display = index === slideIndex ? 'flex' : 'none';
		});

		this.currentSlideIndex = slideIndex;
		this.updateProgress();

		// Check if current slide has video and update slide duration
		const currentSlide = slides[slideIndex];
		const video = currentSlide?.querySelector('.story-slide__video');

		if (video && video.dataset.duration) {
			// Use video duration for this slide
			this.currentSlideDuration = parseInt(video.dataset.duration);
		} else {
			// Use default duration for image slides
			this.currentSlideDuration = this.slideDuration;
		}

		// Auto-play video if current slide has one
		setTimeout(() => {
			this.playCurrentVideo();
		}, 100);

		// Dispatch slide change event
		this.dispatchEvent(
			new CustomEvent('story:slide-changed', {
				detail: { slideIndex, totalSlides: slides.length, duration: this.currentSlideDuration },
			})
		);
	}

	startProgress() {
		this.clearProgress();

		const progressFill = this.querySelector(
			`.stories-modal__progress-bar:nth-child(${this.currentSlideIndex + 1}) .stories-modal__progress-fill`
		);
		if (!progressFill) return;

		// Reset progress fill completely before starting
		progressFill.style.transition = 'none';
		progressFill.style.width = '0%';

		const currentVideo = this.getCurrentVideo();

		if (currentVideo && currentVideo.dataset.duration) {
			// Sync progress with video playback
			this.syncProgressWithVideo(progressFill, currentVideo);
		} else {
			// Standard progress animation for images
			// Force a reflow to ensure 0% is applied before setting transition
			progressFill.offsetHeight;

			progressFill.style.transition = `width ${this.currentSlideDuration}ms linear`;

			// Start progress animation
			requestAnimationFrame(() => {
				progressFill.style.width = '100%';
			});

			// Auto advance to next slide
			this.progressTimer = setTimeout(() => {
				this.nextSlide();
			}, this.currentSlideDuration);
		}
	}

	syncProgressWithVideo(progressFill, video) {
		progressFill.style.width = '0%';
		progressFill.style.transition = 'none';

		// Update progress based on video current time
		const updateVideoProgress = () => {
			if (!video || video.paused || video.ended) return;

			const progress = (video.currentTime / video.duration) * 100;

			progressFill.style.width = `${progress}%`;

			if (video.ended || progress >= 100) {
				this.nextSlide();
				return;
			}
		};

		// Update progress every 100ms
		this.videoProgressInterval = setInterval(updateVideoProgress, 100);

		// Also listen for video end event
		const handleVideoEnd = () => {
			this.clearProgress();
			this.nextSlide();
			video.removeEventListener('ended', handleVideoEnd);
		};

		video.addEventListener('ended', handleVideoEnd);

		// Start updating progress immediately
		updateVideoProgress();
	}

	updateProgress() {
		const progressBars = this.querySelectorAll('.stories-modal__progress-bar');

		progressBars.forEach((bar, index) => {
			const fill = bar.querySelector('.stories-modal__progress-fill');

			if (index < this.currentSlideIndex) {
				fill.style.width = '100%';
				fill.style.transition = 'none';
			} else if (index === this.currentSlideIndex) {
				// Reset current slide progress - will be started by startProgress
				fill.style.width = '0%';
				fill.style.transition = 'none';
			} else {
				fill.style.width = '0%';
				fill.style.transition = 'none';
			}
		});
	}

	nextSlide() {
		this.clearProgress();

		const slides = this.querySelectorAll('.stories-modal__slides-container .story-slide');

		if (this.currentSlideIndex < slides.length - 1) {
			this.showSlide(this.currentSlideIndex + 1);
			this.startProgress();
		} else {
			// Move to next story or close
			if (this.currentStoryIndex < this.stories.length - 1) {
				this.openStory(this.currentStoryIndex + 1);
			} else {
				this.closeModal();
			}
		}
	}

	previousSlide() {
		this.clearProgress();

		if (this.currentSlideIndex > 0) {
			this.showSlide(this.currentSlideIndex - 1);
			this.startProgress();
		} else {
			// Move to previous story
			if (this.currentStoryIndex > 0) {
				// Find the last slide of the previous story
				const prevStory = this.stories[this.currentStoryIndex - 1];
				const prevStoryContent = prevStory.querySelector('.story-content');
				const prevSlides = prevStoryContent.querySelectorAll('.story-slide');

				this.openStory(this.currentStoryIndex - 1);
				// Set to last slide of previous story
				setTimeout(() => {
					this.showSlide(prevSlides.length - 1);
					this.startProgress();
				}, 50);
			}
		}
	}

	toggleProgress() {
		if (this.progressTimer) {
			// Pause
			this.clearProgress();
			const progressFill = this.querySelector(
				`.stories-modal__progress-bar:nth-child(${this.currentSlideIndex + 1}) .stories-modal__progress-fill`
			);

			if (progressFill) {
				const currentWidth = progressFill.style.width;
				progressFill.style.transition = 'none';
				progressFill.style.width = currentWidth;
			}
		} else {
			// Resume
			this.startProgress();
		}
	}

	clearProgress() {
		if (this.progressTimer) {
			clearTimeout(this.progressTimer);
			this.progressTimer = null;
		}

		if (this.videoProgressInterval) {
			clearInterval(this.videoProgressInterval);
			this.videoProgressInterval = null;
		}
	}

	closeModal() {
		this.clearProgress();
		this.pauseAllVideos();
		this.endHoldTimer();
		this.isPaused = false;
		this.isHolding = false;
		this.isDragging = false;
		this.isOpen = false;
		this.modal.style.display = 'none';
		this.modal.classList.remove('stories-paused');

		// Reset modal styles
		this.modal.style.backgroundColor = '';

		const container = this.modal.querySelector('.stories-modal__container');

		if (container) {
			container.style.transform = '';
		}

		// Restore body scroll
		this.restoreBodyScroll();

		// Dispatch close event
		this.dispatchEvent(
			new CustomEvent('story:closed', {
				detail: { storyIndex: this.currentStoryIndex },
			})
		);
	}

	getCurrentVideo() {
		const currentSlide = this.querySelector('.stories-modal__slides-container .story-slide[style*="flex"]');

		return currentSlide ? currentSlide.querySelector('.story-slide__video') : null;
	}

	preventBodyScroll() {
		// Store current scroll position
		this.scrollPosition = window.pageYOffset;

		// Prevent scrolling
		document.body.style.position = 'fixed';
		document.body.style.top = `-${this.scrollPosition}px`;
		document.body.style.width = '100%';
		document.body.style.overflow = 'hidden';
	}

	restoreBodyScroll() {
		// Restore body styles
		document.body.style.position = '';
		document.body.style.top = '';
		document.body.style.width = '';
		document.body.style.overflow = '';

		// Restore scroll position
		if (this.scrollPosition !== undefined) {
			window.scrollTo(0, this.scrollPosition);
		}
	}

	pauseAllVideos() {
		const videos = this.querySelectorAll('.story-slide__video');

		videos.forEach((video) => {
			video.pause();
			video.currentTime = 0;
		});

		// Reset button states
		const playPauseBtn = this.querySelector('.story-video__play-pause');

		if (playPauseBtn) {
			playPauseBtn.classList.remove('playing');
		}
	}

	playCurrentVideo() {
		const video = this.getCurrentVideo();

		if (video) {
			// Reset video to start if it has ended
			if (video.ended) {
				video.currentTime = 0;
			}

			video.play().catch((error) => {
				console.warn('Video autoplay failed:', error);
				// If autoplay fails, the user will need to manually play
			});

			const playPauseBtn = this.querySelector('.story-video__play-pause');

			if (playPauseBtn) {
				playPauseBtn.classList.add('playing');
			}
		}
	}

	// Public API methods
	goToStory(index) {
		if (index >= 0 && index < this.stories.length) {
			this.openStory(index);
		}
	}

	goToSlide(index) {
		const slides = this.querySelectorAll('.stories-modal__slides-container .story-slide');

		if (index >= 0 && index < slides.length) {
			this.clearProgress();
			this.showSlide(index);
			this.startProgress();
		}
	}

	pause() {
		this.clearProgress();
	}

	resume() {
		if (this.isOpen) {
			this.startProgress();
		}
	}
}

// Register the custom element
if (!customElements.get('stories-component')) {
	customElements.define('stories-component', StoriesComponent);
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
	module.exports = StoriesComponent;
}
